"use strict";

import EHIStore from '../store/EHIStore';

const store = new EHIStore();

export class PersistentParameters {
    domainList = [
        location.hostname,
        'essencehealthcare.com',
        'myessencehealthcare.com',
        'wpengine.com',
        'wpenginepowered.com',
        'everythingessence.com',
        'lumeris.io',
        'lumeris.com',
        'essence-ipc.destinationrx.com',
        'essence1.destinationrx.com'
    ];

    campaignData = {
        name: null,
        id: null,
        market: null,
        tfn: null
    };

    pageData = {
        url: new URL(window.location.href),
        params: new URLSearchParams(window.location.search),
        utmParams: new URLSearchParams(),
        gclid: null,
        tfn: null
    };

    managedLinkParams = new WeakMap();
    formDebounceTimers = new WeakMap();

    constructor() {
        this.pageData.url = new URL(window.location.href);
        this.pageData.params = new URLSearchParams(window.location.search);
        this.pageData.gclid = this.pageData.params.get('gclid');
        this.pageData.tfn = this.pageData.params.get('tfn');

        for (const [key, value] of this.pageData.params.entries()) {
            if (key.toLowerCase().startsWith('utm_')) {
                this.pageData.utmParams.set(key, value);
            }
        }

        const domCampaign = this.getCampaignJson();
        if (domCampaign?.id) {
            this.campaignData = domCampaign;
        }

        this.bind();

        this.updateAllElements();
    }

    async init() {
        if (document.readyState === 'loading') {
            await new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve));
        }

        const campaignJson = this.getCampaignJson();
        const storedCampaign = await store.getItem('campaign');

        const isStoredInvalid = !storedCampaign || !storedCampaign.id;

        if (campaignJson) {
            this.campaignData = campaignJson;

            const isDifferent = isStoredInvalid || JSON.stringify(campaignJson) !== JSON.stringify(storedCampaign);

            if (isDifferent) {
                await store.setItem('campaign', campaignJson, 'session');
            }
        } else if (!isStoredInvalid) {
            this.campaignData = storedCampaign;
        } else {
            this.campaignData = null;
        }

        this.syncCampaignFallback();
        this.updateAllElements();
    }

    bind() {
        if (document.readyState === 'interactive' || document.readyState === 'complete') {
            this.updateAllElements();
        } else {
            document.addEventListener('DOMContentLoaded', this.onDOMContentLoaded);
        }

        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('submit', this.onDocumentSubmit, true);
        
        document.addEventListener('gform_post_render', (event) => {
            const formId = event?.detail?.formId;
            if (formId) {
                const target = document.getElementById(`gform_${formId}`) || document.getElementById(`gform_wrapper_${formId}`);
                if (target) this.applyParamsToGravityForms(target);
            }
        });

        if (typeof window.jQuery !== 'undefined') {
            window.jQuery(document).on('gform_post_render', (event, formId) => {
                const target = document.getElementById(`gform_${formId}`) || document.getElementById(`gform_wrapper_${formId}`);
                if (target) this.applyParamsToGravityForms(target);
            });
        }

        window.addEventListener('CookiebotOnAccept', this.onCookiebotConsentChange);
        window.addEventListener('CookiebotOnDecline', this.onCookiebotConsentChange);
        window.addEventListener('CookiebotOnConsentReady', this.onCookiebotConsentChange);
    }

    hasMarketingConsent() {
        if (!this.isAuthorizedCookiebotDomain() || typeof window.Cookiebot === 'undefined' || !window.Cookiebot.consent) {
            return true;
        }
        return Boolean(window.Cookiebot.consent.marketing);
    }

    isAuthorizedCookiebotDomain() {
        if (typeof window.Cookiebot === 'undefined') {
            return true;
        }

        if (window.Cookiebot.loaded && !window.Cookiebot.hasResponse) {
            return false;
        }

        return true;
    }

    isDomainInList(url) {
        return this.domainList.some(
            domain => url.hostname === domain || url.hostname.endsWith(`.${domain}`)
        );
    }

    isGravityForm(element) {
        if (!element) return false;
        const targetForm = element.tagName === 'FORM' ? element : element.querySelector('form');
        return Boolean(
            element.classList?.contains('gform_wrapper') ||
            targetForm?.id?.startsWith('gform_') ||
            targetForm?.querySelector('.gform_fields')
        );
    }

    getCampaignJson() {
        try {
            const el = document.getElementById('ehi-marketing-campaign');
            if (!el || !el.textContent.trim()) return null;
            
            const parsed = JSON.parse(el.textContent);

            return (parsed && (parsed.id || parsed.name)) ? parsed : null;
        } catch (e) {
            console.warn('Failed to parse #ehi-marketing-campaign:', e);
            return null;
        }
    }

    sortSearchParams(searchParams) {
        const getPriority = (key) => {
            if (key === 'utm_campaign') return 1;
            if (key === 'utm_source')   return 2;
            if (key === 'utm_medium')   return 3;
            if (key.startsWith('utm_')) return 4;
            if (key === 'gclid')        return 5;
            if (key === 'tfn')          return 6;

            const customTrackMatch = key.match(/^customtrack([1-5])$/);
            if (customTrackMatch) {
                return 7 + parseInt(customTrackMatch[1], 10) - 1; // Priority 7..11
            }

            return 12; // Catch-all for remaining params
        };

        const entries = Array.from(searchParams.entries());

        entries.sort(([keyA], [keyB]) => {
            const priorityA = getPriority(keyA);
            const priorityB = getPriority(keyB);

            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            return keyA.localeCompare(keyB);
        });

        // Clear existing entries and re-set in sorted order
        Array.from(searchParams.keys()).forEach(key => searchParams.delete(key));
        entries.forEach(([key, value]) => searchParams.append(key, value));
    }

    applyParamsToLink(el) {
        if (!el) return;

        const href = el.getAttribute('href');

        if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
            return;
        }

        let url;
        
        try {
            url = new URL(href, this.pageData.url);
        } catch {
            return;
        }

        if (!this.isDomainInList(url)) {
            return;
        }

        if (el.closest('#wpadminbar')) {
            return;
        }

        let managedParams = this.managedLinkParams.get(el);

        if (!managedParams) {
            managedParams = new Set();
            this.managedLinkParams.set(el, managedParams);
        }

        managedParams.forEach(key => url.searchParams.delete(key));
        managedParams.clear();

        if (!this.hasMarketingConsent()) {
            this.sortSearchParams(url.searchParams);
            el.href = url.toString();
            return;
        }

        const addIfMissing = (key, value) => {
            if (value === null || value === undefined || value === '') return;
            if (url.searchParams.has(key)) return;

            url.searchParams.set(key, value);
            managedParams.add(key);
        };

        this.pageData.utmParams.forEach((value, key) => addIfMissing(key, value));
        addIfMissing('gclid', this.pageData.gclid);
        addIfMissing('tfn', this.pageData.tfn);

        const isEnrollmentPortal = url.hostname === 'destinationrx.com' || url.hostname.endsWith('.destinationrx.com');

        if (isEnrollmentPortal) {
            addIfMissing('customtrack1', this.pageData.utmParams.get('utm_campaign'));
            addIfMissing('customtrack2', this.pageData.utmParams.get('utm_source'));
            addIfMissing('customtrack3', this.pageData.utmParams.get('utm_medium'));
            addIfMissing('customtrack4', this.pageData.tfn);
            addIfMissing('customtrack5', this.pageData.gclid);
        }

        this.sortSearchParams(url.searchParams);
        el.href = url.toString();
    }

    applyParamsToForms(element) {
        if (this.isGravityForm(element)) return;

        const targetForm = element.tagName === 'FORM' ? element : element.querySelector('form');
        if (!targetForm) return;

        const action = targetForm.getAttribute('action') || this.pageData.url.href;
        let url;
        try {
            url = new URL(action, this.pageData.url);
        } catch {
            return;
        }

        if (!this.isDomainInList(url)) return;

        this.injectFormInputs(targetForm, url);
    }

    applyParamsToGravityForms(element) {
        const targetForm = element.tagName === 'FORM' ? element : element.querySelector('form');
        if (!targetForm) return;

        const hasConsent = this.hasMarketingConsent();

        let dynamicFieldInput = targetForm.querySelector('input[name="gform_field_values"]');

        if (!dynamicFieldInput) {
            dynamicFieldInput = document.createElement('input');
            dynamicFieldInput.type = 'hidden';
            dynamicFieldInput.name = 'gform_field_values';
            targetForm.appendChild(dynamicFieldInput);
        }

        const params = new URLSearchParams(dynamicFieldInput.value || '');

        if (hasConsent) {
            if (this.pageData.gclid) params.set('gclid', this.pageData.gclid);
            else params.delete('gclid');

            if (this.pageData.tfn) params.set('tfn', this.pageData.tfn);
            else params.delete('tfn');

            if (this.campaignData?.id && !params.has('campaign_identifier')) {
                params.set('campaign_identifier', this.campaignData.id);
            }
        } else {
            params.delete('gclid');
            params.delete('tfn');
            params.delete('campaign_identifier');
        }

        this.sortSearchParams(params);
        dynamicFieldInput.value = params.toString();

        const parameterizedInputs = targetForm.querySelectorAll('input[data-gform-parameter]');

        parameterizedInputs.forEach(input => {
            const paramKey = input.getAttribute('data-gform-parameter');
            if (!paramKey) return;

            if (!hasConsent) {
                if (['gclid', 'tfn', 'campaign_identifier', 'utm_campaign'].includes(paramKey) || paramKey.startsWith('utm_')) {
                    input.value = '';
                }
                return;
            }

            switch (paramKey) {
                case 'campaign_identifier':
                    if (this.campaignData?.id) {
                        input.value = this.campaignData.id;
                    }
                    break;

                case 'gclid':
                    if (this.pageData.gclid) {
                        input.value = this.pageData.gclid;
                    }
                    break;

                case 'tfn':
                    if (this.pageData.tfn) {
                        input.value = this.pageData.tfn;
                    }
                    break;

                case 'utm_campaign':
                    const utmCampaign = this.pageData.utmParams.get('utm_campaign');
                    if (utmCampaign) {
                        input.value = utmCampaign;
                    }
                    break;

                default:
                    if (paramKey.startsWith('utm_') && this.pageData.utmParams.has(paramKey)) {
                        input.value = this.pageData.utmParams.get(paramKey);
                    }
                    break;
            }
        });
    }

    triggerFormUpdate(form) {
        if (!form) return;

        if (this.formDebounceTimers.has(form)) {
            clearTimeout(this.formDebounceTimers.get(form));
        }

        const timer = setTimeout(() => {
            if (this.isGravityForm(form)) {
                this.applyParamsToGravityForms(form);
            } else {
                this.applyParamsToForms(form);
            }
            this.formDebounceTimers.delete(form);
        }, 50);

        this.formDebounceTimers.set(form, timer);
    }

    syncCampaignFallback() {
        const hasCampaignName = Boolean(this.campaignData?.name);
        const hasUrlUtmCampaign = this.pageData.utmParams.has('utm_campaign');

        if (!hasUrlUtmCampaign && hasCampaignName && this.hasMarketingConsent()) {
            this.pageData.utmParams.set('utm_campaign', this.campaignData.name);
        } else if (!hasUrlUtmCampaign && !this.hasMarketingConsent()) {
            this.pageData.utmParams.delete('utm_campaign');
        }
    }

    injectFormInputs(form, formUrl) {
        const targetUrl = formUrl || this.pageData.url;

        if (this.hasMarketingConsent()) {
            const isEnrollmentPortal = targetUrl.hostname === 'destinationrx.com' || targetUrl.hostname.endsWith('.destinationrx.com');

            if (isEnrollmentPortal) {
                this.appendHiddenInput(form, 'customtrack1', this.pageData.utmParams.get('utm_campaign'));
                this.appendHiddenInput(form, 'customtrack2', this.pageData.utmParams.get('utm_source'));
                this.appendHiddenInput(form, 'customtrack3', this.pageData.utmParams.get('utm_medium'));
                this.appendHiddenInput(form, 'customtrack4', this.pageData.tfn);
                this.appendHiddenInput(form, 'customtrack5', this.pageData.gclid);
            } else {
                this.removeHiddenInput(form, 'customtrack1');
                this.removeHiddenInput(form, 'customtrack2');
                this.removeHiddenInput(form, 'customtrack3');
                this.removeHiddenInput(form, 'customtrack4');
                this.removeHiddenInput(form, 'customtrack5');
            }

            if (this.pageData.utmParams) {
                this.pageData.utmParams.forEach((value, key) => this.appendHiddenInput(form, key, value));
            } else {
                for (const [key, value] of this.pageData.params.entries()) {
                    if (key.toLowerCase().startsWith('utm_')) {
                        this.removeHiddenInput(form, key);
                    }
                }
            }

            if (this.pageData.gclid) {
                this.appendHiddenInput(form, 'gclid', this.pageData.gclid);
            } else {
                this.removeHiddenInput(form, 'gclid');
            }

            if (this.pageData.tfn) {
                this.appendHiddenInput(form, 'tfn', this.pageData.tfn);
            } else {
                this.removeHiddenInput(form, 'tfn');
            }
        } else {
            this.removeHiddenInput(form, 'gclid');
            this.removeHiddenInput(form, 'tfn');
            this.removeHiddenInput(form, 'customtrack1');
            this.removeHiddenInput(form, 'customtrack2');
            this.removeHiddenInput(form, 'customtrack3');
            this.removeHiddenInput(form, 'customtrack4');
            this.removeHiddenInput(form, 'customtrack5');
        }
    }

    appendHiddenInput(form, name, value) {
        let input = form.querySelector(`input[name="${name}"]`);
        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.appendChild(input);
        }
        input.value = value;
    }

    removeHiddenInput(form, name) {
        const input = form.querySelector(`input[name="${name}"]`);
        if (input) input.remove();
    }

    updateAllElements() {
        document.querySelectorAll('a[href]').forEach(el => this.applyParamsToLink(el));

        document.querySelectorAll('form, .gform_wrapper').forEach(element => {
            if (this.isGravityForm(element)) {
                this.applyParamsToGravityForms(element);
            } else {
                this.applyParamsToForms(element);
            }
        });
    }

    onCookiebotConsentChange = (event) => {
        this.syncCampaignFallback();
        this.updateAllElements();
    }

    onDOMContentLoaded = (event) => {
        this.updateAllElements();
    }

    onDocumentClick = (event) => {
        const el = event.target.closest('a');

        if (el && el.href) {
            this.applyParamsToLink(el);
            return;
        }

        const submitBtn = event.target.closest('input[type="submit"], button[type="submit"], .gform_button');

        if (submitBtn) {
            const form = submitBtn.closest('form');

            if (form) {
                this.triggerFormUpdate(form);
            }
        }
    }

    onDocumentSubmit = (event) => {
        const form = event.target.closest('form');
        if (form) {
            this.triggerFormUpdate(form);
        }
    }
}